export enum CaseRecommendationStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

export enum ContactableStatus {
  ATTEMPTED_TODAY = 'Attempted Today',
  NOT_ATTEMPTED_TODAY = 'Not Attempted Today'
}

export enum StartResumeCalling {
  START = 'Start Calling',
  RESUME = 'Resume Calling',
  REFRESH = 'All Cases completed. Refresh to collect more cash'
}

export enum PtpStatus {
  YET_TO_MATURE = 'YET_TO_MATURE',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  DUE_TODAY = 'DUE_TODAY',
  BROKEN_PROMISE = 'BROKEN_PROMISE',
  FULFILLED = 'FULFILLED',
  NO_PTP = 'NO_PTP'
}

export enum PtpStatusLabel {
  YET_TO_MATURE = 'Future PTP',
  PARTIALLY_PAID = 'Partially Paid PTP',
  DUE_TODAY = 'PTP due Today',
  BROKEN_PROMISE = 'Broken Promise',
  FULFILLED = 'Fulfilled PTP',
  NO_PTP = 'No PTP'
}

export enum CaseRecommendationCopy {
  INITIAL_TARGET_MESSAGE = 'Now Easily Achieve your Daily Targets !',
  START_COLLECTIONS = 'Start Collections',
  RESUME_COLLECTIONS = 'Resume Collections',
  COLLECT_MORE_CASH = 'Collect More Cash',
  COMPLETION_MESSAGE = 'Nice work! You have attempted all cases.Hit refresh to maximize your incentive',
  REMAINING_CASES_SUFFIX = 'cases are Left. Hit your target now'
}

export interface RecommendedMetadata {
  totalCases: number;
  visitedCases: number;
  pendingPtps: number;
  lastRefreshedInMs: number;
}

export interface CaseHistory {
  latestDisposition: string;
  ptpState: string;
  isContactable?: boolean;
  latestInteractionTimestamp?: number;
  customerName?: string;
  caseStatus?: string;
  staticEmi?: number;
  feedbackRole?: string;
  feedbackHierarchy?: string;
}

export interface RecommendedCaseDetails {
  caseReferenceId: string;
  isVisited: boolean;
  priority: number;
  attributes: CaseHistory;
}

export interface RecommendedCases {
  lastRefreshedInMs: number;
  caseList: RecommendedCaseDetails[];
}

export interface RecommendedCaseData {
  customerReferenceId: string;
  customerName: string;
  priority: number;
  totalCases: number;
  entityId: string;
  caseReferenceId: string;
  businessVertical: string;
  attributes: CaseHistory;
}

export interface CaseRecommendationState {
  isModalOpen: boolean;
  onStartCallingCallback?: string;
  recommendedCases: RecommendedCases;
  currentRecommendedCase: RecommendedCaseData;
  recommendedMetadata: RecommendedMetadata;
  isLoadingRecommendedCases: boolean;
}

export interface RecommendedListBannerProps {
  primaryText: string;
  secondaryText?: string;
  className?: string;
  onBeforeModalOpen?: () => void;
  imageWidth?: number;
  imageHeight?: number;
  clickstreamEventName: string;
  clickstreamEventDescription: string;
}

export interface params {
  limit: number;
  offset: number;
  successCallback?: () => void;
}

export interface CaseItemProps {
  caseItem: RecommendedCaseDetails;
  index: number;
  totalItems: number;
}

export enum AllocationDpdFilter {
  DPD_1_30 = 'DPD_1_30',
  DPD_30_PLUS = 'DPD_30_PLUS',
  OVERALL = 'OVERALL'
}

export const AllocationDpdFilterLabels: Record<AllocationDpdFilter, string> = {
  [AllocationDpdFilter.DPD_1_30]: '1-30 DPD',
  [AllocationDpdFilter.DPD_30_PLUS]: '30+ DPD',
  [AllocationDpdFilter.OVERALL]: 'Overall'
};

export enum StrategyType {
  NON_CONTACTABLE = 'NON_CONTACTABLE',
  CONTACTABLE_NO_PTP = 'CONTACTABLE_NO_PTP',
  CONTACTABLE_PTP = 'CONTACTABLE_PTP',
  PAID = 'PAID'
}

export const StrategyTypeLabels: Record<StrategyType, string> = {
  [StrategyType.NON_CONTACTABLE]: 'Non Contactable',
  [StrategyType.CONTACTABLE_NO_PTP]: 'Contactable - No PTP',
  [StrategyType.CONTACTABLE_PTP]: 'Contactable - PTP',
  [StrategyType.PAID]: 'Paid'
};

export interface StrategyBreakupItem {
  strategy: string;
  strategyDisplayName: string;
  caseCount: number;
  caseCountMovement: number;
  overdueAmount?: number;
  strategyWiseBreakup?: StrategyBreakupItem[];
}

export interface V2StatsResponse {
  totalCaseCount: number;
  strategyWiseBreakup: StrategyBreakupItem[];
}

export interface V2CaseRecommendationParams {
  strategy: StrategyType;
  allocationDpdFilter: AllocationDpdFilter;
}

export interface StrategyContext {
  strategyType: StrategyType;
  totalCasesInStrategy: number;
  currentPositionInStrategy: number;
  allocationDpdFilter: AllocationDpdFilter;
}

export interface NavigateToCaseOptions {
  strategyType?: StrategyType;
  allocationDpdFilter?: AllocationDpdFilter;
}

export interface StrategyNavigationHeaderProps {
  strategyType: StrategyType;
  totalCasesInStrategy: number;
  currentPositionInStrategy: number;
  allocationDpdFilter: AllocationDpdFilter;
  onFilterChange?: (allocationDpdFilter: AllocationDpdFilter, strategy: StrategyType) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  isPrevDisabled?: boolean;
  isNextDisabled?: boolean;
}

export interface EndOfNavigationModalProps {
  isOpen: boolean;
  currentStrategy?: StrategyType;
  currentDpdFilter: AllocationDpdFilter;
  cohortLabel: string;
  onStartAgain: (allocationDpdFilter: AllocationDpdFilter, strategy: StrategyType) => void;
  onChangeStrategy: (allocationDpdFilter: AllocationDpdFilter, strategy: StrategyType) => void;
  onClose: () => void;
}

export interface ChangeStrategyConfirmModalProps {
  isOpen: boolean;
  targetDpdFilter: AllocationDpdFilter;
  targetStrategy: StrategyType;
  onConfirm: () => void;
  onCancel: () => void;
}

export interface SelectStrategyModalProps {
  isOpen: boolean;
  onConfirm: (dpdFilter: string, strategy: StrategyType) => void;
  onCancel: () => void;
}

export interface StrategyFilterPopperProps {
  currentDpdFilter: AllocationDpdFilter;
  currentStrategy: StrategyType;
  onApply: (dpdFilter: AllocationDpdFilter, strategy: StrategyType) => void;
  showConfirmation?: boolean;
}

export interface ViewingStrategyBannerProps {
  isVisible: boolean;
  dpdFilter: AllocationDpdFilter;
  strategyType: StrategyType;
  onClose: () => void;
}

export interface RecommendedModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export interface PtpSplitData {
  broken: { count: number; amount: number };
  dueToday: { count: number; amount: number };
  future: { count: number; amount: number };
  partiallyPaid: { count: number; amount: number };
  paid: { count: number; amount: number };
}
